import { AppAction } from 'actions/types/ActionTypes'
import { API_CALL } from 'actions/actionConstants'
import createBatchedPromise from 'api/createBatchedPromise'
import { paths } from '@luxuryescapes/contract-business'
import { BUSINESS_TRAVELLER_API_ACTIONS } from 'reducers/businessTravellerApiActionReducers'
import requestPostItemPoliciesCalculatedCredits from 'businessTraveller/api/requestPostItemPoliciesCalculatedCredits'

type ItemPolicyCalculatedCreditsRequestWire = paths['/api/business-traveller/businesses/{businessId}/item-policies/calculate']['post']['requestBody']['content']['application/json']
type ItemPolicyCalculatedCreditsItemInputWire = ItemPolicyCalculatedCreditsRequestWire['items'][number]

type BatchArg = [
  cacheKey: string,
  item: App.BusinessTraveller.ToBeCalculatedItemPolicyItem,
  employeeIds: Array<string>,
]

type ItemPolicyCalculatedCreditsAccumulatedArgs = {
  items: Record<string, ItemPolicyCalculatedCreditsItemInputWire>
  employeeIds: Array<string>
}

type ItemPolicyCalculatedCreditsBatchResponse = Record<
  string,
  App.BusinessTraveller.ItemPolicyCalculatedCreditsResult
>

const requestItemPolicyCalculatedCredits = createBatchedPromise<
  ItemPolicyCalculatedCreditsBatchResponse,
  App.BusinessTraveller.ItemPolicyCalculatedCreditsResult,
  BatchArg,
  ItemPolicyCalculatedCreditsAccumulatedArgs,
  string
>({
  argsReducerFn: (accumulatedArgs, singleArg) => {
    const [, item, employeeIds] = singleArg
    const wireItem = item as ItemPolicyCalculatedCreditsItemInputWire
    const existingItems = accumulatedArgs.items ?? {}
    const existingEmployeeIds = accumulatedArgs.employeeIds ?? []
    return {
      employeeIds: existingEmployeeIds.length > 0 ? existingEmployeeIds : employeeIds,
      items: {
        ...existingItems,
        [item.key]: wireItem,
      },
    }
  },
  batchRequestFn: (batchedArgs, params: string) => {
    const [businessId, employeeIdsKey, approvedRequestId] = params.split('|')
    const employeeIds = employeeIdsKey ? employeeIdsKey.split(',') : batchedArgs.employeeIds
    return requestPostItemPoliciesCalculatedCredits(
      { businessId },
      {
        ...(approvedRequestId ? { approvedRequestId } : {}),
        employeeIds,
        items: Object.values(batchedArgs.items),
      },
    )
  },
  singleValueFn: (singleArg, batchedResponse) => {
    const [, item] = singleArg
    return batchedResponse[item.key]
  },
})

interface Params {
  approvedRequestId?: string
  refetch?: boolean
}

function fetchItemPolicyCalculatedCredits(
  batchArg: BatchArg,
  businessId: string,
  params?: Params,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const [cacheKey, , employeeIds] = batchArg
    const current = state.businessTraveller.itemPolicyCalculatedCredits?.[cacheKey]

    if (!current || params?.refetch) {
      const employeeIdsKey = [...employeeIds].sort().join(',')
      const approvedRequestId = params?.approvedRequestId ?? ''
      dispatch({
        type: API_CALL,
        api: BUSINESS_TRAVELLER_API_ACTIONS.FETCH_ITEM_POLICY_CALCULATED_CREDITS,
        cacheKey,
        request: () => requestItemPolicyCalculatedCredits(batchArg, `${businessId}|${employeeIdsKey}|${approvedRequestId}`),
      })
    }
  }
}

export default fetchItemPolicyCalculatedCredits
